// Text-to-Course panel: pick a difficulty, describe a lesson, and the
// AI writes a full course in the same schema the bundled ones use. The
// result is validated (parseCourse) and handed to App, which adds it
// to the runtime registry so it shows up in the Courses book.

import { useEffect, useState } from "react";

import { ApiError, api } from "../auth/client";
import { useAuth } from "../auth/AuthProvider";
import { parseCourse } from "../courses/parseCourse";
import type { Course, CourseMode } from "../courses/types-and-data";
import { MODE_META, MODES } from "../courses/types-and-data";

const PROMPT_EXAMPLES = [
  "melt a block of iron with lava",
  "dissolve salt in a pool of water",
  "freeze a column of water into ice from the top down",
  "burn a stack of paper and watch the smoke rise",
  "neutralise acid with sodium hydroxide",
  "set off a gunpowder trail with a single spark",
];

export interface CoursePanelProps {
  /** Add a validated, AI-generated course to the registry (App owns
   * the list + persistence) and surface it. */
  onAddCourse: (course: Course) => void;
  /** Open the auth modal when an unauthenticated user tries to use AI. */
  onOpenAuth: (mode: "signin" | "signup") => void;
}

export function CoursePanel({ onAddCourse, onOpenAuth }: CoursePanelProps) {
  const { user } = useAuth();
  const [mode, setMode] = useState<CourseMode>("explorer");
  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exampleIdx, setExampleIdx] = useState(0);

  useEffect(() => {
    if (prompt.length > 0) return;
    const id = setInterval(() => {
      setExampleIdx((i) => (i + 1) % PROMPT_EXAMPLES.length);
    }, 5000);
    return () => clearInterval(id);
  }, [prompt.length]);

  const placeholder =
    PROMPT_EXAMPLES[exampleIdx] ?? PROMPT_EXAMPLES[0] ?? "describe a lesson...";

  const generate = async () => {
    setError(null);
    setStatus(null);
    if (prompt.trim().length < 3) return;
    if (!user) {
      onOpenAuth("signup");
      setError("Sign in to use the AI assistant.");
      return;
    }
    setGenerating(true);
    try {
      const { json } = await api.textToCourse(prompt.trim(), mode);
      const course = parseCourse(json, mode);
      if (!course) {
        setError("The model returned a course we couldn't read — try rephrasing.");
        return;
      }
      onAddCourse(course);
      setStatus(`Added "${course.title}" to ${MODE_META[mode].title}. Open Courses to play it.`);
      setPrompt("");
    } catch (err) {
      if (err instanceof ApiError && err.code === "ai_unavailable") {
        setError("AI is not configured on this deploy (ANTHROPIC_API_KEY missing).");
      } else if (err instanceof ApiError && err.status === 401) {
        onOpenAuth("signin");
        setError("Session expired — sign in again.");
      } else {
        setError(err instanceof Error ? err.message : "AI request failed");
      }
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="course-panel">
      <h2>Text-to-Course</h2>
      <div className="course-mode-row" role="radiogroup" aria-label="Difficulty">
        {MODES.map((m) => (
          <button
            key={m}
            type="button"
            role="radio"
            aria-checked={mode === m}
            className={"course-mode-chip" + (mode === m ? " active" : "")}
            onClick={() => setMode(m)}
            title={MODE_META[m].tagline}
          >
            {MODE_META[m].title}
          </button>
        ))}
      </div>
      <textarea
        className="formula-input"
        rows={3}
        value={prompt}
        placeholder={placeholder}
        onChange={(e) => {
          setPrompt(e.target.value);
          setError(null);
        }}
        spellCheck={false}
      />
      <button
        type="button"
        className="formula-run"
        onClick={generate}
        disabled={generating || prompt.trim().length === 0}
      >
        {generating ? "Writing course..." : "Generate course"}
      </button>
      {error && <div className="formula-status">{error}</div>}
      {status && <div className="formula-status">{status}</div>}
    </div>
  );
}
